<template>
  <div class="test-device-select-input-component">
    <Select
      v-model="selectedIdfa"
      class="test-device-select"
      :options="devicesChoices"
      option-label="label"
      option-value="value"
      placeholder="Select a device"
      :loading="loading"
      :pt="{
        overlay: { class: 'test-device-select-overlay' },
        listContainer: { class: 'test-device-select-dropdown' },
      }"
    />
  </div>
</template>

<script setup lang="ts">
import Select from "primevue/select";
import { computed, ref, watch, watchEffect } from "vue";

import { useTestDeviceSelectInputQuery } from "@/graphql";

const props = defineProps({
  modelValue: {
    type: String,
    default: "",
  },

  noData: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["update:modelValue", "update:noData"]);

const selectedIdfa = ref(props.modelValue);

watch(selectedIdfa, (newValue) => {
  emit("update:modelValue", newValue);
});

// cache-and-network: refresh the list on reopen instead of serving a stale cache.
const { result, loading } = useTestDeviceSelectInputQuery({ fetchPolicy: "cache-and-network" });

const devicesChoices = computed(
  () => result.value?.testDevices.map((device) => ({ label: device.name, value: device.id })) ?? []
);

watchEffect(() => {
  const hasNoData = !loading.value && (result.value?.testDevices ?? []).length === 0;
  emit("update:noData", hasNoData);
});
</script>

<style lang="scss">
.test-device-select-input-component {
  display: flex;
  align-items: center;

  .test-device-select {
    width: 100%;
  }
}

// Lift the body-teleported overlay above Element dialogs (z-index ~2000+);
// !important beats PrimeVue's inline z-index.
.test-device-select-overlay {
  z-index: $z-dropdown !important;

  // PrimeVue pins the panel's min-width to the trigger width but leaves width auto,
  // so it grows to the widest option. width: 0 defers to that min-width, keeping the
  // panel at the trigger width so long device names truncate instead of widening it.
  width: 0;

  .p-select-option-label {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

// Explicit ::-webkit-scrollbar keeps the scrollbar visible on overflow.
// Don't add scrollbar-width/scrollbar-color: that makes Chromium drop these rules.
.test-device-select-dropdown {
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-thumb {
    background: var(--vd-color-gray-light-3, #c0c4cc);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }
}
</style>
